import api from './authService';
import { Project, Revision, CreateProjectForm, CreateRevisionForm, CreateRevisionResponse, User, RevisionName, Document, DocumentRevision, DocumentTimeseries, WorkPackage, WorkUnit, Party, CreateDocumentRevisionForm, UpdateRevisionVersionForm, MetadataPrecheckReusePayload, PendingAttachment, RevisionWorkflow, RevisionActionRequest, MDRData, ProjectStatus, ProjectContractType, Client, ClientKind, WorkflowStep, ProjectType, Discipline, DocumentType, Transmittal, TransmittalDetails, Office, ProjectCompletionFeedbackPayload, CompletionFeedbackSourceDocument, CompletionLetterPayload, ClientFeedbackListItem } from '../types';
import {
    openFilePreviewOrDownload,
    openFilePreviewInNewTab,
    parseContentDispositionFilename,
    triggerBlobDownload,
} from '../utils/filePreview';

export type AuxCategory = 'attachment' | 'archive' | 'crs';

export interface AuxFile {
    id: number;
    category: AuxCategory;
    filename: string;
    description: string;
    file_size: number;
    mime_type?: string | null;
    created_at: string | null;
}

export interface AuxFileUploadEntry {
    file: File;
    category: AuxCategory;
    description: string;
}

export interface ConversionProgress {
    status: string;
    progressMessage: string | null;
    health: 'running' | 'slow' | 'stalled' | null;
    healthMessage: string | null;
    workerName: string | null;
}

export interface ConversionPreviewMeta {
    documentId?: number;
    revisionId?: number;
    onStatus?: (progress: ConversionProgress) => void;
}

export interface ProjectTemplate {
    id: string;
    filename: string;
    size: number;
    mime_type?: string;
    uploaded_at?: string;
}

interface ProjectsService {
    getProjects(): Promise<Project[]>;
    getProjectDetails(projectId: number): Promise<{ project: Project }>;
    assignEngineers(projectId: number, engineerIds: number[]): Promise<{ message: string }>;
    createProject(projectData: CreateProjectForm): Promise<{ message: string; project_id: number }>;
    updateProject(projectId: number, projectData: Partial<CreateProjectForm>): Promise<{ message: string }>;
    getRevisions(projectId: number): Promise<Revision[]>;
    validateProjectRevision(projectId: number, revisionData: CreateRevisionForm): Promise<{ warnings: string[] }>;
    createRevision(projectId: number, revisionData: CreateRevisionForm): Promise<{ message: string; revision_id: number }>;
    getRevisionDetails(projectId: number, revisionId: number): Promise<{ revision: Revision }>;
    getRevisionNames(): Promise<RevisionName[]>;
    getUsers(): Promise<User[]>;
    getProjectUsers(projectId: number): Promise<User[]>;
    getClients(options?: { kind?: ClientKind }): Promise<Client[]>;
    getOffices(): Promise<Office[]>;
    getDocumentsByProject(projectId: number, filters?: {
        nd_number?: string;
        title?: string;
        work_unit?: string;
        work_package?: string;
        document_type?: string;
    }): Promise<Document[]>;
    getDocumentRevisions(projectId: number, documentId: number): Promise<DocumentRevision[]>;
    getDocumentTimeseries(projectId: number, documentId: number): Promise<DocumentTimeseries[]>;
    getDocumentAudit(projectId: number, documentId: number, revisionId?: number): Promise<any[]>;
    getWorkPackages(projectId: number): Promise<WorkPackage[]>;
    getWorkUnits(projectId: number, workPackageId?: number): Promise<WorkUnit[]>;
    getDocumentTypes(): Promise<DocumentType[]>;
    uploadDocument(projectId: number, formData: FormData, generatedPdfBlob?: Blob): Promise<{ message: string; document_id: number }>;
    bulkUploadDocuments(projectId: number, templateFile: File): Promise<{
        message: string;
        results: Array<{ nd_number: string; title: string; document_id: number; status: string }>;
        errors: string[];
    }>;
    downloadTemplate(): Promise<void>;
    getProjectTemplates(projectId: number): Promise<{ templates: ProjectTemplate[] }>;
    uploadProjectTemplates(projectId: number, files: File[]): Promise<{ message: string; templates: ProjectTemplate[] }>;
    deleteProjectTemplate(projectId: number, templateId: string): Promise<void>;
    downloadProjectTemplate(projectId: number, templateId: string, filename: string): Promise<void>;
    getProjectTemplateBlob(projectId: number, templateId: string): Promise<Blob>;
    downloadProjectTemplatesZip(projectId: number): Promise<void>;
    getParties(): Promise<Party[]>;
    getProjectParties(projectId: number): Promise<Party[]>;
    createDocumentRevision(projectId: number, documentId: number, revisionData: CreateDocumentRevisionForm): Promise<{ message: string; revision_id: number }>;
    listRevisionAuxFiles(projectId: number, documentId: number, revisionId: number): Promise<{ files: AuxFile[] }>;
    uploadRevisionAuxFiles(projectId: number, documentId: number, revisionId: number, entries: AuxFileUploadEntry[]): Promise<{ created: AuxFile[] }>;
    deleteRevisionAuxFile(projectId: number, documentId: number, revisionId: number, fileId: number): Promise<void>;
    downloadRevisionAuxFile(projectId: number, documentId: number, revisionId: number, fileId: number, filename: string): Promise<void>;
    getRevisionAuxFileBlob(projectId: number, documentId: number, revisionId: number, fileId: number): Promise<Blob>;
    addStepToRevision(projectId: number, documentId: number, revisionId: number): Promise<{ message: string; new_step: number }>;
    updateRevisionVersion(projectId: number, documentId: number, form: UpdateRevisionVersionForm & { attachments?: PendingAttachment[], archives?: File[], native_files?: File[], crs_files?: File[], on_behalf_of?: number, tempFileId?: string, metadataPrecheckReuse?: MetadataPrecheckReusePayload }): Promise<{ message: string; version_id: number; version_number?: number; validation_warnings?: Array<{ field: string; expected: string; detected: string; severity: string; reason: string }>; extracted_fields?: { status?: string; nd_doc_ref?: string; revision?: string; confidence?: number; source?: string } | null; metadata_validation_executed?: boolean; metadata_validation_skip_reason?: string | null; pdf_stamping_deferred?: boolean }>;
    precheckRevisionMetadata(projectId: number, documentId: number, revisionId: number, mainFile: File): Promise<{ metadata_validation_executed: boolean; metadata_validation_skip_reason?: string | null; validation_warnings?: Array<{ field: string; expected: string; detected: string; severity: string; reason: string }>; extracted_fields?: { status?: string; nd_doc_ref?: string; revision?: string; confidence?: number; source?: string } | null }>;
    getRevisionVersionFiles(projectId: number, documentId: number, revisionId: number, versionId: number): Promise<{ files: any[] }>;
    downloadRevisionFile(projectId: number, documentId: number, revisionId: number, versionId: number, fileId: number, filename: string): Promise<void>;
    getRevisionFileBlob(projectId: number, documentId: number, revisionId: number, versionId: number, fileId: number): Promise<Blob>;
    getRevisionWorkflow(projectId: number, documentId: number, revisionId: number): Promise<RevisionWorkflow>;
    openApproverReviewMainPdfInNewTab(projectId: number, documentId: number, revisionId: number): Promise<void>;
    validateDocumentRevision(projectId: number, documentId: number, revisionId: number, data: RevisionActionRequest): Promise<{ message: string; new_status: string }>;
    rejectRevision(projectId: number, documentId: number, revisionId: number, data: RevisionActionRequest): Promise<{ message: string; new_status: string }>;
    cancelRevision(projectId: number, documentId: number, revisionId: number): Promise<{ message: string; deleted_revision_id: number; storage_cleanup_failed_count?: number }>;
    addRevisionComment(projectId: number, documentId: number, revisionId: number, data: RevisionActionRequest): Promise<{ message: string }>;
    downloadDocumentLatestRevision(projectId: number, documentId: number, lastRevision: string): Promise<void>;
    downloadDocumentVersion(projectId: number, documentId: number, revisionId: number, versionId: number, document?: { nd_number?: string; title?: string }, auditData?: any): Promise<void>;
    configureDocument(projectId: number, documentId: number, config: CreateDocumentRevisionForm): Promise<{ message: string }>;
    getUserPartiesInRevision(projectId: number, documentId: number, revisionId: number): Promise<Array<{
        id: number;
        role: 'author' | 'checker' | 'approver';
        step_order: number;
        status: string;
        party_id: number;
        party_name: string;
        party_trigram?: string;
    }>>;
    getRevisionWorkflowParties(projectId: number, documentId: number, revisionId: number): Promise<Array<{
        id: number;
        party_id: number;
        party_name: string;
        party_trigram?: string;
        role: 'author' | 'checker' | 'approver';
        step_order: number;
        status: string;
    }>>;
    getRevisionBackupParties(projectId: number, documentId: number, revisionId: number): Promise<Array<{
        role: 'author' | 'checker' | 'approver';
        original_party_id: number;
        original_party_name: string;
        backup_party_id: number;
        backup_party_name: string;
    }>>;
    removeBackupParty(projectId: number, documentId: number, revisionId: number, originalPartyId: number, role: 'author' | 'checker' | 'approver'): Promise<{ message: string }>;
    assignBackupParty(projectId: number, documentId: number, revisionId: number, backupPartyId: number, originalPartyId?: number): Promise<{ message: string }>;
    updateMDRRevision(documentId: number, revisionId: number, expectedIssueDate: string | null, workflow?: WorkflowStep[]): Promise<{ message: string }>;
    exportDocuments(projectId: number, format: 'csv' | 'xlsx', filters?: {
        nd_number?: string;
        title?: string;
        work_unit?: string;
        work_package?: string;
        document_type?: string;
    }): Promise<void>;
    getMDR(): Promise<MDRData[]>;
    getMDRByProject(projectId: number): Promise<MDRData[]>;
    getProjectStatuses(): Promise<ProjectStatus[]>;
    getProjectContractTypes(): Promise<ProjectContractType[]>;
    createClient(clientData: {
        name: string;
        kind?: ClientKind;
        is_blacklisted?: boolean;
        main_contact?: string;
        abbreviation?: string;
        pocs?: Array<{ full_name: string; phone?: string; job_position?: string; email?: string }>;
    }): Promise<{ message: string; client_id: number; client?: Client }>;
    updateClient(
        clientId: number,
        clientData: {
            name?: string;
            kind?: ClientKind;
            is_blacklisted?: boolean;
            main_contact?: string;
            abbreviation?: string;
            pocs?: Array<{ full_name: string; phone?: string; job_position?: string; email?: string }>;
        }
    ): Promise<{ message: string }>;
    deleteClient(clientId: number): Promise<{ message: string }>;
    getNextSuggestedId(projectTypeId?: number, year?: number): Promise<{ next_id: number }>;
    getPermissions(projectId: number): Promise<{
        users: User[];
        work_packages: WorkPackage[];
        work_units: WorkUnit[];
        assignments: {
            work_packages: { [userId: number]: number[] };
            work_units: { [userId: number]: number[] };
        };
    }>;
    updatePermissions(projectId: number, assignments: {
        work_packages: { [userId: number]: number[] };
        work_units: { [userId: number]: number[] };
    }): Promise<{ message: string }>;
    getUserPermissions(userId?: number): Promise<{
        work_packages: { [projectId: number]: number[] };
        work_units: { [projectId: number]: number[] };
    }>;
    getProjectTypes(): Promise<ProjectType[]>;
    getDisciplines(): Promise<Discipline[]>;
    updateDocument(projectId: number, documentId: number, data: any): Promise<{ message: string }>;
    cancelDocument(projectId: number, documentId: number): Promise<{ message: string; document_id: number; watermark_failed_count?: number }>;
    getNextDocumentSuggestedId(
        projectId: number,
        disciplineId?: number,
        documentTypeId?: number,
        workPackageId?: number,
        workUnitId?: number
    ): Promise<{ next_id: number }>;
    convertPreview(file: File, meta?: ConversionPreviewMeta): Promise<{ blob: Blob, tempFileId: string | null, metadataValidationExecuted?: boolean, metadataValidationSkipReason?: string | null, extractedFields?: { status?: string; nd_doc_ref?: string; revision?: string; confidence?: number; source?: string } | null, validationWarnings?: Array<{ field: string; expected: string; detected: string; severity: string; reason: string }> }>;
    getDocumentContext(documentId: number): Promise<{ projectId: number; documentId: number }>;
    getTransmittalContext(transmittalId: number): Promise<{ projectId: number; transmittalId: number }>;
    getDashboardKPI(projectId?: number): Promise<{
        documentStatuses: { name: string; value: number; color: string }[];
        activeProjects: number;
        pendingActions: number;
        overdueItems: number;
        documentStatusSummary: {
            approved: number;
            pending: number;
            cancelled: number;
        };
        plannedVsActual: {
            plannedProgress: number;
            actualProgress: number;
            plannedDocuments: number;
            actualDocuments: number;
            totalDocuments: number;
        };
    }>;
    getClientContact(projectId: number): Promise<{ client_name: string; email: string; poc_emails?: string[] }>;
    sendTransmittal(projectId: number, documentId: number, revisionId: number, data: {
        main_recipients: { name: string; email: string }[];
        other_recipients: { name: string; email: string }[];
        file_ids: number[];
        message?: string;
        expires_in_days?: number;
    }): Promise<{ message: string; transmittal_id: number }>;
    getProjectTransmittals(projectId: number): Promise<Transmittal[]>;
    deactivateTransmittal(projectId: number, transmittalId: number): Promise<{ message: string }>;
    getTransmittal(token: string): Promise<TransmittalDetails>;
    downloadTransmittalFiles(token: string, fileIds: number[]): Promise<{ autoAcknowledged: boolean }>;
    acknowledgeTransmittal(token: string, ackKey: string): Promise<{ message: string }>;
    downloadTransmittalReceptionVerification(projectId: number, transmittalId: number, transmittalNumber?: string | null): Promise<void>;
    setTransmittalComment(projectId: number, transmittalId: number, comment: string): Promise<{ message: string; internal_comment: string | null }>;
    deleteTransmittalComment(projectId: number, transmittalId: number): Promise<{ message: string }>;
    updateTransmittalStatus(projectId: number, transmittalId: number, status: string): Promise<{ message: string; status: string }>;
    updateTransmittalRecipientEmail(projectId: number, transmittalId: number, type: 'poc' | 'cc', recipientId: number, email: string): Promise<{ message: string; email: string }>;
    resendTransmittalEmails(projectId: number, transmittalId: number, pocRecipientIds: number[], ccRecipientIds: number[]): Promise<{ message: string; results: { poc: any[]; cc: any[] } }>;
    ocrPrefillCompletionFeedback(projectId: number, file: File): Promise<{ fields: Record<string, unknown>; ocr_markdown_preview?: string }>;
    submitCompletionFeedback(projectId: number, payload: ProjectCompletionFeedbackPayload, sourceDocument?: File | null): Promise<{ message: string; id: number; created_at: string | null }>;
    updateCompletionFeedback(projectId: number, feedbackId: number, payload: ProjectCompletionFeedbackPayload): Promise<{ message: string; id: number; created_at: string | null }>;
    downloadCompletionFeedbackSourceDocument(projectId: number, feedbackId: number, fallbackFilename?: string): Promise<void>;
    listCompletionFeedback(projectId: number, limit?: number): Promise<{
        items: Array<{
            id: number;
            submitted_by_user_id: number;
            submitted_by_name: string;
            payload: ProjectCompletionFeedbackPayload;
            created_at: string | null;
            source_document?: CompletionFeedbackSourceDocument | null;
        }>;
    }>;
    listAllCompletionFeedback(): Promise<{ items: ClientFeedbackListItem[] }>;
    getCompletionLetterDefaults(projectId: number): Promise<CompletionLetterPayload>;
    generateCompletionLetter(projectId: number, payload: CompletionLetterPayload): Promise<void>;
}

function transmittalAckClientContext(): {
    timezone: string;
    device: {
        platform?: string;
        mobile?: boolean;
        screen?: string;
        pixelRatio?: number;
    };
} {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const uaData = (navigator as Navigator & {
        userAgentData?: { platform?: string; mobile?: boolean };
    }).userAgentData;
    const width = window.screen?.width;
    const height = window.screen?.height;
    const ratio = window.devicePixelRatio;
    return {
        timezone,
        device: {
            platform: uaData?.platform || navigator.platform || undefined,
            mobile: typeof uaData?.mobile === 'boolean' ? uaData.mobile : undefined,
            screen: width && height ? `${width}x${height}` : undefined,
            pixelRatio: typeof ratio === 'number' ? ratio : undefined,
        },
    };
}

export const projectsService: ProjectsService = {
    // Get all projects
    async getProjects(): Promise<Project[]> {
        const response = await api.get('/projects');
        return response.data.projects || [];
    },

    // Get project details
    async getProjectDetails(projectId: number): Promise<{ project: Project }> {
        const response = await api.get(`/projects/${projectId}`);
        return response.data;
    },

    // Assign engineers to project
    async assignEngineers(projectId: number, engineerIds: number[]): Promise<{ message: string }> {
        const response = await api.post(`/projects/${projectId}/engineers`, { engineer_ids: engineerIds });
        return response.data;
    },

    // Create a new project
    async createProject(projectData: CreateProjectForm): Promise<{ message: string; project_id: number }> {
        const response = await api.post('/projects', projectData);
        return response.data;
    },

    // Update a project
    async updateProject(projectId: number, projectData: Partial<CreateProjectForm>): Promise<{ message: string }> {
        const response = await api.put(`/projects/${projectId}`, projectData);
        return response.data;
    },

    // Get revisions for a specific project
    async getRevisions(projectId: number): Promise<Revision[]> {
        const response = await api.get(`/projects/${projectId}/revisions`);
        return response.data.revisions || [];
    },

    // Validate a project revision creation
    async validateProjectRevision(projectId: number, revisionData: CreateRevisionForm): Promise<{ warnings: string[] }> {
        const response = await api.post(`/projects/${projectId}/revisions/validate`, revisionData);
        return response.data;
    },

    // Create a new revision
    async createRevision(projectId: number, revisionData: CreateRevisionForm): Promise<{ message: string; revision_id: number }> {
        const response = await api.post(`/projects/${projectId}/revisions`, revisionData);
        return response.data;
    },

    // Get revision details
    async getRevisionDetails(projectId: number, revisionId: number): Promise<{ revision: Revision }> {
        const response = await api.get(`/projects/${projectId}/revisions/${revisionId}`);
        return response.data;
    },

    // Get revision names for dropdowns
    async getRevisionNames(): Promise<RevisionName[]> {
        const response = await api.get('/projects/revision-names');
        return response.data.revision_names || [];
    },

    // Get users list for dropdowns
    async getUsers(): Promise<User[]> {
        const response = await api.get('/projects/users');
        return response.data.users || [];
    },

    // Get users related to a project (PM and engineers)
    async getProjectUsers(projectId: number): Promise<User[]> {
        const response = await api.get(`/projects/${projectId}/users`);
        return response.data.users || [];
    },

    // Get clients for dropdowns
    async getClients(options?: { kind?: ClientKind }): Promise<Client[]> {
        const params = options?.kind ? { kind: options.kind } : undefined;
        const response = await api.get('/projects/clients', { params });
        return response.data.clients || [];
    },

    // Get offices for dropdowns
    async getOffices(): Promise<Office[]> {
        const response = await api.get('/auth/offices');
        return response.data.offices || [];
    },

    // Get documents for a project with optional filters
    async getDocumentsByProject(projectId: number, filters?: {
        nd_number?: string;
        title?: string;
        work_unit?: string;
        work_package?: string;
        document_type?: string;
    }): Promise<Document[]> {
        const params = new URLSearchParams();
        if (filters?.nd_number) params.append('nd_number', filters.nd_number);
        if (filters?.title) params.append('title', filters.title);
        if (filters?.work_unit) params.append('work_unit', filters.work_unit);
        if (filters?.work_package) params.append('work_package', filters.work_package);
        if (filters?.document_type) params.append('document_type', filters.document_type);

        const query = params.toString() ? `?${params.toString()}` : '';
        const response = await api.get(`/projects/${projectId}/documents${query}`);
        return response.data.documents || [];
    },

    // Get revisions for a specific document
    async getDocumentRevisions(projectId: number, documentId: number): Promise<DocumentRevision[]> {
        const response = await api.get(`/projects/${projectId}/documents/${documentId}/revisions`);
        return response.data.revisions || [];
    },

    // Get timeseries data for a specific document
    async getDocumentTimeseries(projectId: number, documentId: number): Promise<DocumentTimeseries[]> {
        const response = await api.get(`/projects/${projectId}/documents/${documentId}/timeseries`);
        return response.data.timeseries || [];
    },

    // Get full audit trail for a specific document
    async getDocumentAudit(projectId: number, documentId: number, revisionId?: number): Promise<any[]> {
        const params = revisionId ? `?revision_id=${revisionId}` : '';
        const response = await api.get(`/projects/${projectId}/documents/${documentId}/audit${params}`);
        return response.data.audit || [];
    },

    // Get work packages for a project
    async getWorkPackages(projectId: number): Promise<WorkPackage[]> {
        const response = await api.get(`/projects/${projectId}/work-packages`);
        return response.data.work_packages || [];
    },

    // Get work units for a project
    async getWorkUnits(projectId: number, workPackageId?: number): Promise<WorkUnit[]> {
        const params = new URLSearchParams();
        if (workPackageId) params.append('work_package_id', workPackageId.toString());

        const query = params.toString() ? `?${params.toString()}` : '';
        const response = await api.get(`/projects/${projectId}/work-units${query}`);
        return response.data.work_units || [];
    },

    // Get document types
    async getDocumentTypes(): Promise<DocumentType[]> {
        const response = await api.get('/projects/document-types');
        return response.data.document_types;
    },

    // Upload a document
    async uploadDocument(projectId: number, formData: FormData, generatedPdfBlob?: Blob): Promise<{ message: string; document_id: number }> {
        if (generatedPdfBlob) {
            formData.append('generated_pdf', generatedPdfBlob, 'initial.pdf');
        }

        const response = await api.post(`/projects/${projectId}/documents/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Bulk upload documents
    async bulkUploadDocuments(projectId: number, templateFile: File): Promise<{
        message: string;
        results: Array<{ nd_number: string; title: string; document_id: number; status: string }>;
        errors: string[];
    }> {
        const formData = new FormData();
        formData.append('template_file', templateFile);

        const response = await api.post(`/projects/${projectId}/documents/bulk-upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Download bulk upload template
    async downloadTemplate(): Promise<void> {
        const response = await api.get('/projects/download-template', {
            responseType: 'blob',
        });

        openFilePreviewOrDownload(
            new Blob([response.data]),
            'bulk_document_upload_template.xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );
    },

    async getProjectTemplates(projectId: number): Promise<{ templates: ProjectTemplate[] }> {
        const response = await api.get(`/projects/${projectId}/templates`);
        return response.data;
    },

    async uploadProjectTemplates(projectId: number, files: File[]): Promise<{ message: string; templates: ProjectTemplate[] }> {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        const response = await api.post(`/projects/${projectId}/templates`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    async deleteProjectTemplate(projectId: number, templateId: string): Promise<void> {
        await api.delete(`/projects/${projectId}/templates/${templateId}`);
    },

    async getProjectTemplateBlob(projectId: number, templateId: string): Promise<Blob> {
        const response = await api.get(`/projects/${projectId}/templates/${templateId}/download`, {
            responseType: 'blob',
        });

        if (response.headers['content-type']?.includes('application/json')) {
            const text = await response.data.text();
            try {
                const data = JSON.parse(text);
                if (data.download_url) {
                    const remoteResponse = await fetch(data.download_url);
                    return await remoteResponse.blob();
                }
            } catch {
                // proceed as blob
            }
        }

        return new Blob([response.data]);
    },

    async downloadProjectTemplate(projectId: number, templateId: string, filename: string): Promise<void> {
        const blob = await this.getProjectTemplateBlob(projectId, templateId);
        openFilePreviewOrDownload(blob, filename);
    },

    async downloadProjectTemplatesZip(projectId: number): Promise<void> {
        const response = await api.get(`/projects/${projectId}/templates/download-all`, {
            responseType: 'blob',
        });

        let filename = 'project_templates.zip';
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
            const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (matches && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }

        triggerBlobDownload(new Blob([response.data]), filename);
    },

    // Get parties (checkers/approvers)
    async getParties(): Promise<Party[]> {
        const response = await api.get('/parties');
        return response.data || [];
    },

    // Get parties related to a specific project
    async getProjectParties(projectId: number): Promise<Party[]> {
        const response = await api.get(`/projects/${projectId}/parties`);
        return response.data.parties || [];
    },

    // Create a document revision
    async createDocumentRevision(projectId: number, documentId: number, revisionData: CreateDocumentRevisionForm): Promise<{ message: string; revision_id: number }> {
        const response = await api.post(`/projects/${projectId}/documents/${documentId}/revisions`, revisionData);
        return response.data;
    },

    async listRevisionAuxFiles(
        projectId: number,
        documentId: number,
        revisionId: number
    ): Promise<{ files: AuxFile[] }> {
        const response = await api.get(
            `/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/auxiliary-files`
        );
        return response.data;
    },

    async uploadRevisionAuxFiles(
        projectId: number,
        documentId: number,
        revisionId: number,
        entries: AuxFileUploadEntry[]
    ): Promise<{ created: AuxFile[] }> {
        const formData = new FormData();
        const categories: AuxCategory[] = [];
        const descriptions: string[] = [];
        entries.forEach((entry) => {
            formData.append('files', entry.file);
            categories.push(entry.category);
            descriptions.push(entry.description || '');
        });
        formData.append('categories', JSON.stringify(categories));
        formData.append('descriptions', JSON.stringify(descriptions));
        const response = await api.post(
            `/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/auxiliary-files`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    },

    async deleteRevisionAuxFile(
        projectId: number,
        documentId: number,
        revisionId: number,
        fileId: number
    ): Promise<void> {
        await api.delete(
            `/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/auxiliary-files/${fileId}`
        );
    },

    async downloadRevisionAuxFile(
        projectId: number,
        documentId: number,
        revisionId: number,
        fileId: number,
        filename: string
    ): Promise<void> {
        const blob = await this.getRevisionAuxFileBlob(projectId, documentId, revisionId, fileId);
        openFilePreviewOrDownload(blob, filename);
    },

    async getRevisionAuxFileBlob(
        projectId: number,
        documentId: number,
        revisionId: number,
        fileId: number
    ): Promise<Blob> {
        const response = await api.get(
            `/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/auxiliary-files/${fileId}/download`,
            { responseType: 'blob' }
        );

        const blob = response.data as Blob;
        const contentType = String(response.headers['content-type'] || '').toLowerCase();
        const isLikelyJsonEnvelope =
            contentType.includes('application/json') ||
            blob.type.includes('application/json') ||
            (blob.size > 0 && blob.size <= 1024 * 1024);

        if (!isLikelyJsonEnvelope) {
            return blob;
        }

        try {
            const text = (await blob.text()).trim();
            if (!text || (!text.startsWith('{') && !text.startsWith('['))) {
                return blob;
            }

            const data = JSON.parse(text) as { download_url?: string };
            if (!data.download_url) {
                return blob;
            }

            const externalResponse = await fetch(data.download_url);
            if (!externalResponse.ok) {
                throw new Error('Failed to fetch file from storage');
            }
            return await externalResponse.blob();
        } catch (error) {
            throw error instanceof Error
                ? error
                : new Error('Failed to resolve storage download URL');
        }
    },

    // Add a step to an existing document revision
    async addStepToRevision(projectId: number, documentId: number, revisionId: number): Promise<{ message: string; new_step: number }> {
        const response = await api.post(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/add-step`);
        return response.data;
    },

    // Update a revision version (upload new file version)
    async updateRevisionVersion(projectId: number, documentId: number, form: UpdateRevisionVersionForm & { attachments?: PendingAttachment[], archives?: File[], native_files?: File[], crs_files?: File[], on_behalf_of?: number, tempFileId?: string, metadataPrecheckReuse?: MetadataPrecheckReusePayload }): Promise<{ message: string; version_id: number; version_number?: number; validation_warnings?: Array<{ field: string; expected: string; detected: string; severity: string; reason: string }>; extracted_fields?: { status?: string; nd_doc_ref?: string; revision?: string; confidence?: number; source?: string } | null; metadata_validation_executed?: boolean; metadata_validation_skip_reason?: string | null; pdf_stamping_deferred?: boolean }> {
        const formData = new FormData();
        formData.append('revision_id', form.revision_id.toString());
        if (form.file) {
            formData.append('main_file', form.file);
        }
        const precheckReuse = form.metadataPrecheckReuse;
        if (precheckReuse?.fingerprint) {
            formData.append('metadata_precheck_reuse', '1');
            formData.append('metadata_precheck_fingerprint', precheckReuse.fingerprint);
            formData.append('metadata_precheck_executed', precheckReuse.executed ? '1' : '0');
            if (precheckReuse.skipReason) {
                formData.append('metadata_precheck_skip_reason', precheckReuse.skipReason);
            }
            formData.append('metadata_precheck_warnings', JSON.stringify(precheckReuse.validationWarnings || []));
            formData.append(
                'metadata_precheck_extracted_fields',
                JSON.stringify(precheckReuse.extractedFields ?? null)
            );
        }
        if (form.attachments) {
            form.attachments.forEach(item => formData.append('attachments', item.file));
            formData.append(
                'attachment_appendice',
                JSON.stringify(form.attachments.map(item => item.isAppendice))
            );
        }
        if (form.archives) {
            form.archives.forEach(f => formData.append('archives', f));
        }
        if (form.native_files) {
            form.native_files.forEach(f => formData.append('native_files', f));
        }
        if (form.crs_files) {
            form.crs_files.forEach(f => formData.append('crs_files', f));
        }
        if (form.on_behalf_of) {
            formData.append('on_behalf_of', form.on_behalf_of.toString());
        }
        if (form.tempFileId) {
            formData.append('temp_file_id', form.tempFileId);
        }
        formData.append('notes', form.notes);

        const response = await api.post(`/projects/${projectId}/documents/${documentId}/revisions/update-version`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 600000,
        });
        return response.data;
    },

    async precheckRevisionMetadata(
        projectId: number,
        documentId: number,
        revisionId: number,
        mainFile: File
    ): Promise<{
        metadata_validation_executed: boolean;
        metadata_validation_skip_reason?: string | null;
        validation_warnings?: Array<{ field: string; expected: string; detected: string; severity: string; reason: string }>;
        extracted_fields?: { status?: string; nd_doc_ref?: string; revision?: string; confidence?: number; source?: string } | null;
    }> {
        const formData = new FormData();
        formData.append('revision_id', String(revisionId));
        formData.append('main_file', mainFile);
        const response = await api.post(
            `/projects/${projectId}/documents/${documentId}/revisions/metadata-precheck`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    },

    async getRevisionVersionFiles(
        projectId: number,
        documentId: number,
        revisionId: number,
        versionId: number
    ): Promise<{ files: any[] }> {
        const response = await api.get(
            `/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/versions/${versionId}/files`
        );
        return response.data;
    },

    async convertPreview(file: File, meta?: ConversionPreviewMeta): Promise<{ blob: Blob, tempFileId: string | null, metadataValidationExecuted?: boolean, metadataValidationSkipReason?: string | null, extractedFields?: { status?: string; nd_doc_ref?: string; revision?: string; confidence?: number; source?: string } | null, validationWarnings?: Array<{ field: string; expected: string; detected: string; severity: string; reason: string }> }> {
        const formData = new FormData();
        formData.append('file', file);
        // Append IDs when provided (even if they could be 0).
        if (meta?.documentId !== undefined) formData.append('document_id', String(meta.documentId));
        if (meta?.revisionId !== undefined) formData.append('revision_id', String(meta.revisionId));

        const response = await api.post('/projects/convert-preview', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            responseType: 'blob',
            // Allow us to inspect JSON error bodies even though responseType is 'blob'.
            validateStatus: () => true,
        });

        const contentType = response.headers['content-type'] || '';

        if (response.status >= 400) {
            if (contentType.includes('application/json')) {
                const payloadText = await response.data.text();
                try {
                    const payload = JSON.parse(payloadText);
                    throw new Error(payload?.error || payload?.message || 'Conversion failed');
                } catch {
                    throw new Error('Conversion failed');
                }
            }

            throw new Error(`Conversion failed (HTTP ${response.status})`);
        }

        const parseHeaderJson = (headerName: string) => {
            const raw = response.headers[headerName] || response.headers[headerName.toLowerCase()];
            if (!raw) return null;
            try {
                return JSON.parse(decodeURIComponent(raw));
            } catch {
                return null;
            }
        };

        if (!contentType.includes('application/json')) {
            const tempFileId = response.headers['x-temporary-file-id'] || null;
            const metadataValidationSkipReason = response.headers['x-metadata-validation-skip-reason'] || null;
            return {
                blob: response.data,
                tempFileId,
                metadataValidationExecuted: (response.headers['x-metadata-validation-executed'] || '0') === '1',
                metadataValidationSkipReason,
                extractedFields: parseHeaderJson('x-extracted-fields'),
                validationWarnings: parseHeaderJson('x-metadata-validation-warnings') || [],
            };
        }

        const payloadText = await response.data.text();
        const taskPayload = JSON.parse(payloadText);
        const taskId: string = taskPayload.task_id;
        let tempFileId: string | null = taskPayload.temp_file_id || null;
        const pollIntervalMs = 6000;
        const maxWaitMs = 20 * 60 * 1000; // 20 minutes
        const start = Date.now();

        const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

        while (Date.now() - start < maxWaitMs) {
            let statusData: any = null;
            try {
                const statusResponse = await api.get(`/projects/convert-preview/${taskId}/status`);
                statusData = statusResponse.data;
            } catch (err: any) {
                // If status polling is rate-limited by any upstream component,
                // honor Retry-After when provided and keep polling.
                if (err?.response?.status === 429) {
                    const retryAfterRaw = err?.response?.headers?.['retry-after'];
                    const retryAfterSeconds = Number.parseInt(retryAfterRaw || '0', 10);
                    const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
                        ? retryAfterSeconds * 1000
                        : pollIntervalMs;
                    await sleep(waitMs);
                    continue;
                }
                throw err;
            }

            meta?.onStatus?.({
                status: statusData.status,
                progressMessage: statusData.progress_message || null,
                health: statusData.health || null,
                healthMessage: statusData.health_message || null,
                workerName: statusData.worker_name || null,
            });

            if (statusData.status === 'completed') {
                const query = new URLSearchParams();
                if (meta?.documentId !== undefined) query.set('document_id', String(meta.documentId));
                if (meta?.revisionId !== undefined) query.set('revision_id', String(meta.revisionId));
                const downloadResponse = await api.get(`/projects/convert-preview/${taskId}/download?${query.toString()}`, {
                    responseType: 'blob',
                });
                tempFileId = downloadResponse.headers['x-temporary-file-id'] || tempFileId;
                const parseDownloadHeaderJson = (headerName: string) => {
                    const raw = downloadResponse.headers[headerName] || downloadResponse.headers[headerName.toLowerCase()];
                    if (!raw) return null;
                    try {
                        return JSON.parse(decodeURIComponent(raw));
                    } catch {
                        return null;
                    }
                };
                const metadataValidationSkipReason = downloadResponse.headers['x-metadata-validation-skip-reason'] || null;
                return {
                    blob: downloadResponse.data,
                    tempFileId,
                    metadataValidationExecuted: (downloadResponse.headers['x-metadata-validation-executed'] || '0') === '1',
                    metadataValidationSkipReason,
                    extractedFields: parseDownloadHeaderJson('x-extracted-fields'),
                    validationWarnings: parseDownloadHeaderJson('x-metadata-validation-warnings') || [],
                };
            }

            if (statusData.status === 'failed') {
                throw new Error(statusData.error || 'Conversion failed');
            }

            await sleep(pollIntervalMs);
        }

        throw new Error('Conversion timed out while waiting for completion');
    },

    async downloadRevisionFile(
        projectId: number,
        documentId: number,
        revisionId: number,
        versionId: number,
        fileId: number,
        filename: string
    ): Promise<void> {
        try {
            const blob = await this.getRevisionFileBlob(
                projectId,
                documentId,
                revisionId,
                versionId,
                fileId
            );
            openFilePreviewOrDownload(blob, filename);
        } catch (error) {
            console.error('Download failed:', error);
            throw error;
        }
    },

    async getRevisionFileBlob(
        projectId: number,
        documentId: number,
        revisionId: number,
        versionId: number,
        fileId: number
    ): Promise<Blob> {
        const response = await api.get(
            `/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/versions/${versionId}/files/${fileId}/download`,
            { responseType: 'blob', validateStatus: () => true }
        );

        const blob = response.data as Blob;
        const contentType = String(response.headers['content-type'] || '').toLowerCase();
        if (response.status >= 400) {
            if (contentType.includes('application/json')) {
                const text = await blob.text();
                try {
                    const payload = JSON.parse(text);
                    throw new Error(payload?.error || `Download failed (HTTP ${response.status})`);
                } catch (err) {
                    if (err instanceof Error) throw err;
                }
            }
            throw new Error(`Download failed (HTTP ${response.status})`);
        }
        const isLikelyJsonEnvelope =
            contentType.includes('application/json') ||
            blob.type.includes('application/json') ||
            (blob.size > 0 && blob.size <= 1024 * 1024);

        if (!isLikelyJsonEnvelope) {
            return blob;
        }

        try {
            const text = (await blob.text()).trim();
            if (!text || (!text.startsWith('{') && !text.startsWith('['))) {
                return blob;
            }

            const data = JSON.parse(text) as { download_url?: string };
            if (!data.download_url) {
                return blob;
            }

            const externalResponse = await fetch(data.download_url);
            if (!externalResponse.ok) {
                throw new Error('Failed to fetch file from storage');
            }
            return await externalResponse.blob();
        } catch (error) {
            // Do not silently return JSON envelope blobs; they create tiny/corrupt ZIP entries.
            throw error instanceof Error
                ? error
                : new Error('Failed to resolve storage download URL');
        }
    },

    // Get revision workflow information
    async getRevisionWorkflow(projectId: number, documentId: number, revisionId: number): Promise<RevisionWorkflow> {
        const response = await api.get(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/workflow`);
        return response.data;
    },

    /** Stream latest main PDF with JWT (blob URL) and open in a new tab. */
    async openApproverReviewMainPdfInNewTab(projectId: number, documentId: number, revisionId: number): Promise<void> {
        try {
            const response = await api.get(
                `/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/approver-review-main`,
                { responseType: 'blob' }
            );
            const blob = response.data as Blob;
            const filename = parseContentDispositionFilename(
                response.headers['content-disposition'],
                'document.pdf'
            );
            openFilePreviewInNewTab(blob, filename, 'application/pdf');
        } catch (e: unknown) {
            const err = e as { response?: { data?: Blob } };
            const data = err?.response?.data;
            if (data instanceof Blob) {
                try {
                    const text = await data.text();
                    const j = JSON.parse(text) as { error?: string };
                    throw new Error(j.error || 'Failed to load document for review');
                } catch {
                    throw new Error('Failed to load document for review');
                }
            }
            throw e;
        }
    },

    // Validate a document revision (checker or approver action)
    async validateDocumentRevision(projectId: number, documentId: number, revisionId: number, data: RevisionActionRequest): Promise<{ message: string; new_status: string }> {
        const response = await api.post(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/validate`, data);
        return response.data;
    },

    // Reject a revision (checker or approver)
    async rejectRevision(projectId: number, documentId: number, revisionId: number, data: RevisionActionRequest): Promise<{ message: string; new_status: string }> {
        const hasFiles = data.file || (data.attachments && data.attachments.length > 0) || (data.archives && data.archives.length > 0) || (data.native_files && data.native_files.length > 0) || (data.crs_files && data.crs_files.length > 0);
        if (hasFiles) {
            const formData = new FormData();
            formData.append('comment', data.comment || '');
            if (data.file) {
                formData.append('file', data.file);
            }
            if (data.attachments) {
                data.attachments.forEach(item => formData.append('attachments', item.file));
                formData.append(
                    'attachment_appendice',
                    JSON.stringify(data.attachments.map(item => item.isAppendice))
                );
            }
            if (data.archives) {
                data.archives.forEach(f => formData.append('archives', f));
            }
            if (data.native_files) {
                data.native_files.forEach(f => formData.append('native_files', f));
            }
            if (data.crs_files) {
                data.crs_files.forEach(f => formData.append('crs_files', f));
            }
            if (data.on_behalf_of) {
                formData.append('on_behalf_of', data.on_behalf_of.toString());
            }
            const response = await api.post(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/reject`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } else {
            const payload: any = { comment: data.comment || '' };
            if (data.on_behalf_of) {
                payload.on_behalf_of = data.on_behalf_of;
            }
            const response = await api.post(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/reject`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        }
    },

    // Cancel (hard-delete) a non-approved revision (admin or project PM only)
    async cancelRevision(projectId: number, documentId: number, revisionId: number): Promise<{ message: string; deleted_revision_id: number; storage_cleanup_failed_count?: number }> {
        const response = await api.delete(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}`);
        return response.data;
    },

    // Add a comment to a revision
    async addRevisionComment(projectId: number, documentId: number, revisionId: number, data: RevisionActionRequest): Promise<{ message: string }> {
        const response = await api.post(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/comment`, data);
        return response.data;
    },

    // Download the latest version of the last revision for a document (simplified)
    async downloadDocumentLatestRevision(projectId: number, documentId: number, lastRevision: string): Promise<void> {
        const downloadUrl = `/projects/${projectId}/documents/${documentId}/download-latest?last_revision=${encodeURIComponent(lastRevision)}`;
        const response = await api.get(downloadUrl, { responseType: 'blob', validateStatus: () => true });

        let filename = 'document.pdf';
        const contentType = String(response.headers['content-type'] || '').toLowerCase();
        let blob = response.data as Blob;

        if (response.status >= 400) {
            if (contentType.includes('application/json')) {
                const payload = JSON.parse(await blob.text()) as { error?: string };
                throw new Error(payload.error || `Download failed (HTTP ${response.status})`);
            }
            throw new Error(`Download failed (HTTP ${response.status})`);
        }

        if (contentType.includes('application/json') || blob.type.includes('application/json')) {
            const data = JSON.parse(await blob.text()) as { download_url?: string; filename?: string };
            filename = data.filename || filename;
            if (data.download_url) {
                const remoteResponse = await fetch(data.download_url);
                if (!remoteResponse.ok) {
                    throw new Error('Failed to fetch file from storage');
                }
                blob = await remoteResponse.blob();
            }
        } else {
            filename = parseContentDispositionFilename(response.headers['content-disposition'], filename);
        }

        openFilePreviewOrDownload(blob, filename);
    },

    // Download a specific version of a document revision
    async downloadDocumentVersion(projectId: number, documentId: number, revisionId: number, versionId: number, document?: { nd_number?: string; title?: string }, auditData?: any): Promise<void> {
        const downloadUrl = `/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/versions/${versionId}/download`;
        const response = await api.get(downloadUrl, { responseType: 'blob', validateStatus: () => true });

        let filename = 'document.pdf';
        const contentType = String(response.headers['content-type'] || '').toLowerCase();
        let blob = response.data as Blob;

        if (response.status >= 400) {
            if (contentType.includes('application/json')) {
                const payload = JSON.parse(await blob.text()) as { error?: string };
                throw new Error(payload.error || `Download failed (HTTP ${response.status})`);
            }
            throw new Error(`Download failed (HTTP ${response.status})`);
        }

        if (contentType.includes('application/json') || blob.type.includes('application/json')) {
            const data = JSON.parse(await blob.text()) as { download_url?: string; filename?: string };
            filename = data.filename || filename;
            if (data.download_url) {
                const remoteResponse = await fetch(data.download_url);
                if (!remoteResponse.ok) {
                    throw new Error('Failed to fetch file from storage');
                }
                blob = await remoteResponse.blob();
            }
        } else {
            filename = parseContentDispositionFilename(response.headers['content-disposition'], filename);
        }

        openFilePreviewOrDownload(blob, filename);
    },

    // Update document metadata
    async updateDocument(projectId: number, documentId: number, data: any): Promise<{ message: string }> {
        const response = await api.put(`/projects/${projectId}/documents/${documentId}`, data);
        return response.data;
    },

    // Cancel a document (admin or PM only)
    async cancelDocument(projectId: number, documentId: number): Promise<{ message: string; document_id: number; watermark_failed_count?: number }> {
        const response = await api.post(`/projects/${projectId}/documents/${documentId}/cancel`);
        return response.data;
    },

    // Configure document (update workflow, backup parties, expected issue date)
    async configureDocument(projectId: number, documentId: number, config: CreateDocumentRevisionForm): Promise<{ message: string }> {
        const response = await api.post(`/projects/${projectId}/documents/${documentId}/configure`, config);
        return response.data;
    },

    // Get user's party assignments in a revision
    async getUserPartiesInRevision(projectId: number, documentId: number, revisionId: number): Promise<Array<{
        id: number;
        role: 'author' | 'checker' | 'approver';
        step_order: number;
        status: string;
        party_id: number;
        party_name: string;
        party_trigram?: string;
    }>> {
        const response = await api.get(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/user-parties`);
        return response.data.user_parties || [];
    },

    // Get all workflow parties for a revision
    async getRevisionWorkflowParties(projectId: number, documentId: number, revisionId: number): Promise<Array<{
        id: number;
        party_id: number;
        party_name: string;
        party_trigram?: string;
        role: 'author' | 'checker' | 'approver';
        step_order: number;
        status: string;
    }>> {
        const response = await api.get(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/workflow-parties`);
        return response.data.workflow_parties || [];
    },

    // Get existing backup parties for a revision
    async getRevisionBackupParties(projectId: number, documentId: number, revisionId: number): Promise<Array<{
        role: 'author' | 'checker' | 'approver';
        original_party_id: number;
        original_party_name: string;
        backup_party_id: number;
        backup_party_name: string;
    }>> {
        const response = await api.get(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/backup-parties`);
        return response.data.backup_parties || [];
    },

    // Remove a backup party assignment
    async removeBackupParty(projectId: number, documentId: number, revisionId: number, originalPartyId: number, role: 'author' | 'checker' | 'approver'): Promise<{ message: string }> {
        const response = await api.post(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/remove-backup`, {
            original_party_id: originalPartyId,
            role,
        });
        return response.data;
    },

    // Assign a backup party for the current user's role or for a specific party (document_controller)
    async assignBackupParty(projectId: number, documentId: number, revisionId: number, backupPartyId: number, originalPartyId?: number): Promise<{ message: string }> {
        const response = await api.post(`/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/assign-backup`, {
            backup_party_id: backupPartyId,
            original_party_id: originalPartyId,
        });
        return response.data;
    },

    // Update MDR revision expected issue date and workflow
    async updateMDRRevision(documentId: number, revisionId: number, expectedIssueDate: string | null, workflow?: WorkflowStep[]): Promise<{ message: string }> {
        const payload: any = {
            document_id: documentId,
            revision_id: revisionId,
            expected_issue_date: expectedIssueDate,
        };

        // Transform workflow if provided
        if (workflow && workflow.length > 0) {
            const checkers: any[] = [];
            const approvers: any[] = [];
            let authorPartyId: number | undefined = undefined;

            workflow.forEach((step: WorkflowStep, index: number) => {
                if (step.role === 'checker' && step.party_id) {
                    checkers.push({ party_id: step.party_id, step_order: index + 1 });
                } else if (step.role === 'approver' && step.party_id) {
                    approvers.push({ party_id: step.party_id, step_order: index + 1 });
                } else if (step.role === 'author' && step.party_id) {
                    authorPartyId = step.party_id;
                }
            });

            payload.checkers = checkers;
            payload.approvers = approvers;
            payload.author_party_id = authorPartyId;
        }

        const response = await api.post('/projects/mdr/update-revision', payload);
        return response.data;
    },

    // Export documents to CSV or XLSX
    async exportDocuments(projectId: number, format: 'csv' | 'xlsx', filters?: {
        nd_number?: string;
        title?: string;
        work_unit?: string;
        work_package?: string;
        document_type?: string;
    }): Promise<void> {
        const params = new URLSearchParams();
        if (filters?.nd_number) params.append('nd_number', filters.nd_number);
        if (filters?.title) params.append('title', filters.title);
        if (filters?.work_unit) params.append('work_unit', filters.work_unit);
        if (filters?.work_package) params.append('work_package', filters.work_package);
        if (filters?.document_type) params.append('document_type', filters.document_type);

        const query = params.toString();
        const response = await api.get(`/projects/${projectId}/documents/export?${query}`, {
            responseType: 'blob'
        });

        openFilePreviewOrDownload(new Blob([response.data]), `documents.${format}`);
    },


    // Get MDR (Master Document Register) data
    async getMDR(): Promise<MDRData[]> {
        const response = await api.get('/projects/mdr');
        return response.data.mdr_data || [];
    },

    async getMDRByProject(projectId: number): Promise<MDRData[]> {
        const response = await api.get('/projects/mdr', {
            params: { project_id: projectId }
        });
        return response.data.mdr_data || [];
    },

    // Get project statuses
    async getProjectStatuses(): Promise<ProjectStatus[]> {
        const response = await api.get('/projects/statuses');
        return response.data.statuses || [];
    },

    // Get project contract types
    async getProjectContractTypes(): Promise<ProjectContractType[]> {
        const response = await api.get('/projects/contract-types');
        return response.data.contract_types || [];
    },

    // Create a new client
    async createClient(clientData: {
        name: string;
        kind?: ClientKind;
        is_blacklisted?: boolean;
        main_contact?: string;
        abbreviation?: string;
        pocs?: Array<{ full_name: string; phone?: string; job_position?: string; email?: string }>;
    }): Promise<{ message: string; client_id: number; client?: Client }> {
        const response = await api.post('/projects/clients', clientData);
        return response.data;
    },

    // Update an existing client
    async updateClient(
        clientId: number,
        clientData: {
            name?: string;
            kind?: ClientKind;
            is_blacklisted?: boolean;
            main_contact?: string;
            abbreviation?: string;
            pocs?: Array<{ full_name: string; phone?: string; job_position?: string; email?: string }>;
        }
    ): Promise<{ message: string }> {
        const response = await api.put(`/projects/clients/${clientId}`, clientData);
        return response.data;
    },

    // Delete a client
    async deleteClient(clientId: number): Promise<{ message: string }> {
        const response = await api.delete(`/projects/clients/${clientId}`);
        return response.data;
    },

    // Get next suggested project counter, optionally scoped by project type and year
    async getNextSuggestedId(projectTypeId?: number, year?: number): Promise<{ next_id: number }> {
        const params =
            projectTypeId != null && year != null
                ? { project_type_id: projectTypeId, year }
                : {};
        const response = await api.get('/projects/next-suggested-id', { params });
        return response.data;
    },

    // Get project permissions
    async getPermissions(projectId: number): Promise<{
        users: User[];
        work_packages: WorkPackage[];
        work_units: WorkUnit[];
        assignments: {
            work_packages: { [userId: number]: number[] };
            work_units: { [userId: number]: number[] };
        };
    }> {
        const response = await api.get(`/projects/${projectId}/permissions`);
        return response.data;
    },

    // Update project permissions
    async updatePermissions(projectId: number, assignments: {
        work_packages: { [userId: number]: number[] };
        work_units: { [userId: number]: number[] };
    }): Promise<{ message: string }> {
        const response = await api.post(`/projects/${projectId}/permissions`, { assignments });
        return response.data;
    },

    // Get current user's permissions, or specific user's if userId is provided (PM/Admin only)
    async getUserPermissions(userId?: number): Promise<{
        work_packages: { [projectId: number]: number[] };
        work_units: { [projectId: number]: number[] };
    }> {
        const params = userId ? { user_id: userId } : {};
        const response = await api.get('/projects/user-permissions', { params });
        return response.data;
    },
    // Get project types
    async getProjectTypes(): Promise<ProjectType[]> {
        const response = await api.get('/projects/project-types');
        return response.data.project_types || [];
    },
    // Get disciplines
    async getDisciplines(): Promise<Discipline[]> {
        const response = await api.get('/projects/disciplines');
        return response.data.disciplines || [];
    },


    // Get next suggested document ID
    async getNextDocumentSuggestedId(
        projectId: number,
        disciplineId?: number,
        documentTypeId?: number,
        workPackageId?: number,
        workUnitId?: number
    ): Promise<{ next_id: number }> {
        const params = new URLSearchParams();
        if (disciplineId) params.append('discipline_id', disciplineId.toString());
        if (documentTypeId) params.append('document_type_id', documentTypeId.toString());
        if (workPackageId != null) params.append('work_package_id', workPackageId.toString());
        if (workUnitId != null) params.append('work_unit_id', workUnitId.toString());
        const query = params.toString() ? `?${params.toString()}` : '';
        const response = await api.get(`/projects/${projectId}/next-document-suggested-id${query}`);
        return response.data;
    },



    // Get document context (project ID)
    async getDocumentContext(documentId: number): Promise<{ projectId: number; documentId: number }> {
        const response = await api.get(`/projects/documents/${documentId}/context`);
        return response.data;
    },

    async getTransmittalContext(transmittalId: number): Promise<{ projectId: number; transmittalId: number }> {
        const response = await api.get(`/projects/transmittals/${transmittalId}/context`);
        return response.data;
    },

    // Get dashboard KPI data
    async getDashboardKPI(projectId?: number): Promise<{
        documentStatuses: { name: string; value: number; color: string }[];
        activeProjects: number;
        pendingActions: number;
        overdueItems: number;
        documentStatusSummary: {
            approved: number;
            pending: number;
            cancelled: number;
        };
        plannedVsActual: {
            plannedProgress: number;
            actualProgress: number;
            plannedDocuments: number;
            actualDocuments: number;
            totalDocuments: number;
        };
    }> {
        const params = projectId ? { project_id: projectId } : {};
        const response = await api.get('/projects/dashboard/kpi', { params });
        return response.data;
    },

    // Get client contact info for a project
    async getClientContact(projectId: number): Promise<{ client_name: string; email: string }> {
        const response = await api.get(`/projects/${projectId}/client-contact`);
        return response.data;
    },

    // Send transmittal email with document download links (supports multiple main recipients)
    async sendTransmittal(projectId: number, documentId: number, revisionId: number, data: {
        main_recipients: { name: string; email: string }[];
        other_recipients: { name: string; email: string }[];
        file_ids: number[];
        message?: string;
        expires_in_days?: number;
    }): Promise<{ message: string; transmittal_id: number }> {
        const response = await api.post(
            `/projects/${projectId}/documents/${documentId}/revisions/${revisionId}/transmit`,
            data
        );
        return response.data;
    },

    // Get project transmittals
    async getProjectTransmittals(projectId: number): Promise<Transmittal[]> {
        const response = await api.get(`/projects/${projectId}/transmittals`);
        return response.data.transmittals || [];
    },

    // Deactivate a specific transmittal
    async deactivateTransmittal(projectId: number, transmittalId: number): Promise<{ message: string }> {
        const response = await api.post(`/projects/${projectId}/transmittals/${transmittalId}/deactivate`);
        return response.data;
    },

    // Public: Get transmittal details
    async getTransmittal(token: string): Promise<TransmittalDetails> {
        const response = await api.get(`/transmittals/${token}`);
        return response.data;
    },

    // Public: Download transmittal files — returns { autoAcknowledged } when main doc is downloaded.
    // Backend always streams file bytes; acknowledgement fires only after successful storage read.
    async downloadTransmittalFiles(token: string, fileIds: number[]): Promise<{ autoAcknowledged: boolean }> {
        try {
            const response = await api.post(`/transmittals/${token}/download`, {
                file_ids: fileIds,
                ...transmittalAckClientContext(),
            }, {
                responseType: 'blob'
            });

            // If the backend returned a JSON error, surface it
            if (response.headers['content-type']?.includes('application/json')) {
                const text = await (response.data as Blob).text();
                try {
                    const data = JSON.parse(text);
                    if (data.error) throw new Error(data.error);
                } catch (e) { /* not JSON, proceed */ }
            }

            let filename = 'transmittal_documents';
            const contentDisposition = response.headers['content-disposition'];
            filename = parseContentDispositionFilename(contentDisposition, filename);

            const blob = new Blob([response.data]);
            openFilePreviewOrDownload(blob, filename);

            // Auto-acknowledged only if backend confirmed storage read succeeded
            const autoAcknowledged = response.headers['x-auto-acknowledged'] === 'true';
            return { autoAcknowledged };
        } catch (error) {
            console.error('Download failed:', error);
            throw error;
        }
    },

    // Public: Acknowledge receipt (requires ack_key from URL)
    async acknowledgeTransmittal(token: string, ackKey: string): Promise<{ message: string }> {
        const response = await api.post(`/transmittals/${token}/acknowledge`, {
            ack_key: ackKey,
            ...transmittalAckClientContext(),
        });
        return response.data;
    },

    async downloadTransmittalReceptionVerification(
        projectId: number,
        transmittalId: number,
        transmittalNumber?: string | null,
    ): Promise<void> {
        const response = await api.get(
            `/projects/${projectId}/transmittals/${transmittalId}/reception-verification`,
            { responseType: 'blob' },
        );

        if (response.headers['content-type']?.includes('application/json')) {
            const text = await (response.data as Blob).text();
            try {
                const data = JSON.parse(text);
                if (data.error) throw new Error(data.error);
            } catch (error) {
                if (error instanceof Error && error.message) {
                    throw error;
                }
            }
        }

        const fallback = `transmittal_reception_verification_${transmittalNumber || transmittalId}.pdf`;
        const filename = parseContentDispositionFilename(response.headers['content-disposition'], fallback);
        triggerBlobDownload(new Blob([response.data], { type: 'application/pdf' }), filename, 'application/pdf');
    },

    // Set or update the internal comment for a transmittal
    async setTransmittalComment(projectId: number, transmittalId: number, comment: string): Promise<{ message: string; internal_comment: string | null }> {
        const response = await api.put(`/projects/${projectId}/transmittals/${transmittalId}/comment`, { comment });
        return response.data;
    },

    // Delete the internal comment for a transmittal
    async deleteTransmittalComment(projectId: number, transmittalId: number): Promise<{ message: string }> {
        const response = await api.delete(`/projects/${projectId}/transmittals/${transmittalId}/comment`);
        return response.data;
    },

    // Update transmittal status
    async updateTransmittalStatus(projectId: number, transmittalId: number, status: string): Promise<{ message: string; status: string }> {
        const response = await api.patch(`/projects/${projectId}/transmittals/${transmittalId}/status`, { status });
        return response.data;
    },

    // Update a recipient's email address (resets email_status to 'pending')
    async updateTransmittalRecipientEmail(
        projectId: number,
        transmittalId: number,
        type: 'poc' | 'cc',
        recipientId: number,
        email: string
    ): Promise<{ message: string; email: string }> {
        const response = await api.put(
            `/projects/${projectId}/transmittals/${transmittalId}/recipients/${type}/${recipientId}/email`,
            { email }
        );
        return response.data;
    },

    // Resend emails to specific recipients (POC and/or CC)
    async resendTransmittalEmails(
        projectId: number,
        transmittalId: number,
        pocRecipientIds: number[],
        ccRecipientIds: number[]
    ): Promise<{ message: string; results: { poc: any[]; cc: any[] } }> {
        const response = await api.post(
            `/projects/${projectId}/transmittals/${transmittalId}/resend`,
            { poc_recipient_ids: pocRecipientIds, cc_recipient_ids: ccRecipientIds }
        );
        return response.data;
    },

    async ocrPrefillCompletionFeedback(projectId: number, file: File): Promise<{ fields: Record<string, unknown>; ocr_markdown_preview?: string }> {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`/projects/${projectId}/completion-feedback/ocr-prefill`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    async submitCompletionFeedback(projectId: number, payload: ProjectCompletionFeedbackPayload, sourceDocument?: File | null): Promise<{ message: string; id: number; created_at: string | null }> {
        if (sourceDocument) {
            const formData = new FormData();
            formData.append('payload', JSON.stringify(payload));
            formData.append('source_document', sourceDocument);
            const response = await api.post(`/projects/${projectId}/completion-feedback`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data;
        }
        const response = await api.post(`/projects/${projectId}/completion-feedback`, payload);
        return response.data;
    },

    async updateCompletionFeedback(projectId: number, feedbackId: number, payload: ProjectCompletionFeedbackPayload): Promise<{ message: string; id: number; created_at: string | null }> {
        const response = await api.put(`/projects/${projectId}/completion-feedback/${feedbackId}`, payload);
        return response.data;
    },

    async downloadCompletionFeedbackSourceDocument(projectId: number, feedbackId: number, fallbackFilename = `feedback-source-${feedbackId}`): Promise<void> {
        const response = await api.get(`/projects/${projectId}/completion-feedback/${feedbackId}/source-document`, {
            responseType: 'blob',
            validateStatus: () => true,
        });

        const contentType = response.headers['content-type'] || '';
        if (response.status >= 400 || contentType.includes('application/json')) {
            if (contentType.includes('application/json')) {
                const payloadText = await response.data.text();
                try {
                    const payload = JSON.parse(payloadText) as { download_url?: string; error?: string };
                    if (payload?.download_url) {
                        const remoteResponse = await fetch(payload.download_url);
                        if (!remoteResponse.ok) {
                            throw new Error('Failed to fetch file from storage');
                        }
                        const blob = await remoteResponse.blob();
                        openFilePreviewOrDownload(blob, fallbackFilename);
                        return;
                    }
                    throw new Error(payload?.error || 'Failed to download source document');
                } catch (error) {
                    if (error instanceof Error) throw error;
                    throw new Error('Failed to download source document');
                }
            }
            throw new Error(`Failed to download source document (HTTP ${response.status})`);
        }

        let filename = fallbackFilename;
        filename = parseContentDispositionFilename(response.headers['content-disposition'], filename);

        openFilePreviewOrDownload(new Blob([response.data]), filename);
    },

    async listCompletionFeedback(projectId: number, limit = 20): Promise<{
        items: Array<{
            id: number;
            submitted_by_user_id: number;
            submitted_by_name: string;
            payload: ProjectCompletionFeedbackPayload;
            created_at: string | null;
            source_document?: CompletionFeedbackSourceDocument | null;
        }>;
    }> {
        const response = await api.get(`/projects/${projectId}/completion-feedback`, { params: { limit } });
        return response.data;
    },

    async listAllCompletionFeedback(): Promise<{ items: ClientFeedbackListItem[] }> {
        const response = await api.get<{ items: ClientFeedbackListItem[] }>('/projects/completion-feedback');
        return response.data;
    },

    async getCompletionLetterDefaults(projectId: number): Promise<CompletionLetterPayload> {
        const response = await api.get(`/projects/${projectId}/completion-letter/defaults`);
        return response.data;
    },

    async generateCompletionLetter(projectId: number, payload: CompletionLetterPayload): Promise<void> {
        const response = await api.post(`/projects/${projectId}/completion-letter`, payload, {
            responseType: 'blob',
        });

        if (response.headers['content-type']?.includes('application/json')) {
            const text = await (response.data as Blob).text();
            try {
                const data = JSON.parse(text);
                if (data.error) throw new Error(data.error);
            } catch (error) {
                if (error instanceof Error && error.message) {
                    throw error;
                }
            }
        }

        const fallback = `${payload.project_nd_number || 'project'} - Project Completion Letter.pdf`;
        const filename = parseContentDispositionFilename(response.headers['content-disposition'], fallback);
        triggerBlobDownload(new Blob([response.data], { type: 'application/pdf' }), filename, 'application/pdf');
    },

};

export default projectsService;